import { useState, useEffect, useLayoutEffect, useRef } from "react";
import type { CSSProperties, ReactNode, RefObject } from "react";
import {
  ChevronDown,
  X,
  CircleX,
  ListFilter,
  Calendar,
  ArrowUpDown,
  Check,
  type LucideIcon,
} from "lucide-react";
import { cn } from "../utils/cn";
import { AeraButton, CardScrollRail, ModalHeader, ModalShell } from "@aera/ui";
import type { TableFilters, WorkerSchema } from "../types";
import { resolveStatusVisual } from "../utils/statusVisuals";
import { ResolvedIcon, type IconOverrideMap } from "../utils/iconRegistry";
import { useElementWidth } from "../hooks/useElementWidth";
import { DataTableSearchField } from "./DataTableSearchField";
import { DATA_LAYOUT_DESKTOP_MIN_PX, useWideDataLayout } from "../hooks/useWideDataLayout";
import { stripWorkerLabelEmoji } from "../utils/workerIcons";
import { SIMILAR_NAME_FILTER_KEY } from "../utils/similarNameChips";

export const FILTER_BAR_WIDE_BREAKPOINT = DATA_LAYOUT_DESKTOP_MIN_PX;
const FILTER_MODAL_CENTER_MIN_WIDTH = DATA_LAYOUT_DESKTOP_MIN_PX;

function useFilterMdViewport() {
  const [matches, setMatches] = useState(false);

  useEffect(() => {
    const mq = window.matchMedia(
      `(min-width: ${FILTER_MODAL_CENTER_MIN_WIDTH}px)`,
    );
    const update = () => setMatches(mq.matches);
    update();
    mq.addEventListener("change", update);
    return () => mq.removeEventListener("change", update);
  }, []);

  return matches;
}

function filterDropdownPanelClass(widthClass: string) {
  return cn(
    "aera-dt-filter-dropdown z-9997 rounded-lg border border-aera-lightgray/80 bg-aera-white p-1 animate-in fade-in zoom-in-95 duration-200",
    widthClass,
  );
}

function FilterMenuShell({
  title,
  isOpen,
  isWideLayout,
  onClose,
  menuRef,
  anchorRef,
  outerRef,
  width,
  widthClass,
  footer,
  children,
}: {
  title: string;
  isOpen: boolean;
  isWideLayout: boolean;
  onClose: () => void;
  menuRef: RefObject<HTMLDivElement | null>;
  anchorRef: RefObject<HTMLElement | null>;
  outerRef: RefObject<HTMLElement | null>;
  width: string;
  widthClass: string;
  footer?: ReactNode;
  children: ReactNode;
}) {
  const isCenteredModal = useFilterMdViewport();
  const menuStyle = useAnchoredFilterMenuStyle(
    anchorRef,
    outerRef,
    width,
    isOpen,
    isWideLayout,
  );

  if (isWideLayout) {
    if (!isOpen) {
      return null;
    }

    return (
      <div
        ref={menuRef}
        className={filterDropdownPanelClass(widthClass)}
        style={{ boxShadow: "var(--aera-dt-shadow-soft)", ...menuStyle }}
      >
        <div className="aera-dt-filter-desktop-title px-4 py-2">
          <span className="text-xs font-bold uppercase tracking-widest text-aera-charcoal">
            {title}
          </span>
        </div>
        {children}
        {footer}
      </div>
    );
  }

  return (
    <ModalShell
      isOpen={isOpen}
      onClose={onClose}
      align={isCenteredModal ? "center" : "bottom"}
      zIndexClass="z-[10040]"
      panelClassName={cn(
        "flex max-h-[min(88dvh,40rem)] flex-col overflow-hidden bg-aera-white shadow-xl",
        isCenteredModal
          ? "w-full max-w-md rounded-2xl"
          : "rounded-t-2xl border-t border-aera-lightgray",
      )}
    >
      {!isCenteredModal ? (
        <div className="flex shrink-0 justify-center pb-1 pt-2">
          <div
            className="h-1 w-10 rounded-full bg-aera-lightgray"
            aria-hidden
          />
        </div>
      ) : null}

      <ModalHeader
        title={title}
        onClose={onClose}
        surface={false}
        className={cn(
          "border-b border-aera-lightgray px-4 sm:px-4",
          isCenteredModal ? "pb-3" : "pb-3 pt-0",
        )}
      />

      <div
        ref={menuRef}
        className={cn(
          "aera-dt-filter-sheet-body min-h-0 flex-1 overflow-y-auto overscroll-contain bg-aera-white",
          footer ? "px-4 py-4" : "p-1",
          !footer &&
            (isCenteredModal
              ? "pb-4"
              : "pb-[max(0.75rem,env(safe-area-inset-bottom))]"),
        )}
      >
        {children}
      </div>
      {footer ? (
        <div
          className={cn(
            "shrink-0 border-t border-aera-lightgray bg-aera-white px-4 py-3",
            !isCenteredModal && "pb-[max(0.75rem,env(safe-area-inset-bottom))]",
          )}
        >
          {footer}
        </div>
      ) : null}
    </ModalShell>
  );
}

export interface FilterBarProps {
  schema: WorkerSchema;
  filters: TableFilters;
  setFilter: (key: string, value: string | null) => void;
  searchInputRef?: RefObject<HTMLInputElement | null>;
  selectionState?: {
    count: number;
    onClear: () => void;
    onBulkCancel?: () => void;
  };
  iconOverrides?: IconOverrideMap;
  containerWidth?: number;
  /** Display names used to build similar-name chips under search/filter. */
  similarNameLabels?: string[];
  /** Desktop mockup: “แสดง 1–28 จาก 28” on the right of ตัวกรอง. */
  rangeLabel?: string;
  /** Makes the ตัวกรอง trigger easier to spot (e.g. งานเงิน queue). */
  filterButtonVariant?: "transparent" | "outline";
  /** Skip built-in search field (parent already hosts search, e.g. งานเงิน). */
  hideSearch?: boolean;
  /** Controlled open state for the ตัวกรอง sheet/modal. */
  filterMenuOpen?: boolean;
  onFilterMenuOpenChange?: (open: boolean) => void;
  "data-testid"?: string;
}

function toIsoDate(date: Date) {
  const year = date.getFullYear();
  const month = `${date.getMonth() + 1}`.padStart(2, "0");
  const day = `${date.getDate()}`.padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function getDatePresetRange(preset: string): {
  from: string | null;
  to: string | null;
} {
  const now = new Date();
  const end = new Date(now);

  switch (preset) {
    case "วันนี้": {
      const today = toIsoDate(now);
      return { from: today, to: today };
    }
    case "7 วันที่ผ่านมา": {
      const start = new Date(now);
      start.setDate(start.getDate() - 6);
      return { from: toIsoDate(start), to: toIsoDate(end) };
    }
    case "เดือนนี้": {
      const start = new Date(now.getFullYear(), now.getMonth(), 1);
      return { from: toIsoDate(start), to: toIsoDate(end) };
    }
    case "เดือนที่แล้ว": {
      const start = new Date(now.getFullYear(), now.getMonth() - 1, 1);
      const lastDay = new Date(now.getFullYear(), now.getMonth(), 0);
      return { from: toIsoDate(start), to: toIsoDate(lastDay) };
    }
    default:
      return { from: null, to: null };
  }
}

function getActiveDateLabel(filters: TableFilters, datePresets: string[]) {
  if (!filters.from && !filters.to) return null;

  for (const preset of datePresets) {
    if (preset === "กำหนดเอง") continue;
    const range = getDatePresetRange(preset);
    if (range.from === filters.from && range.to === filters.to) {
      return preset;
    }
  }

  if (filters.from && filters.to) return `${filters.from} ถึง ${filters.to}`;
  return filters.from ?? filters.to;
}

function matchingDatePreset(
  from: string | null | undefined,
  to: string | null | undefined,
  presets: string[],
): string {
  if (!from && !to) return "ทั้งหมด";
  for (const preset of presets) {
    if (preset === "กำหนดเอง") continue;
    const range = getDatePresetRange(preset);
    if (range.from === from && range.to === to) return preset;
  }
  return "กำหนดเอง";
}

function FilterSectionLabel({ children }: { children: ReactNode }) {
  return (
    <p className="mb-2 text-xs font-semibold text-aera-gray">{children}</p>
  );
}

function FilterChoiceChip({
  selected,
  onClick,
  children,
}: {
  selected: boolean;
  onClick: () => void;
  children: ReactNode;
}) {
  return (
    <AeraButton
      type="button"
      variant={selected ? "warm" : "outline"}
      size="xs"
      fontWeight="medium"
      aria-pressed={selected}
      onClick={onClick}
      className="max-w-full min-w-0 shrink-0 rounded-full! px-2.5!"
    >
      <span className="truncate">{children}</span>
    </AeraButton>
  );
}

function FilterChoiceRow({
  selected,
  onClick,
  children,
}: {
  selected: boolean;
  onClick: () => void;
  children: ReactNode;
}) {
  return (
    <AeraButton
      type="button"
      variant="transparent"
      size="sm"
      fontWeight="medium"
      fullWidth
      aria-pressed={selected}
      onClick={onClick}
      className={cn(
        "justify-between! rounded-xl! px-3! py-2.5!",
        selected
          ? "bg-aera-warmcream text-aera-charcoal"
          : "text-aera-charcoal hover:bg-aera-offwhite",
      )}
      iconRight={
        selected ? (
          <Check className="h-4 w-4 text-aera-amber" strokeWidth={3} />
        ) : undefined
      }
    >
      {children}
    </AeraButton>
  );
}

export function FilterBar({
  schema,
  filters,
  setFilter,
  searchInputRef,
  selectionState,
  iconOverrides,
  containerWidth: _containerWidth,
  rangeLabel,
  filterButtonVariant = "transparent",
  hideSearch = false,
  filterMenuOpen,
  onFilterMenuOpenChange,
  "data-testid": dataTestId,
}: FilterBarProps) {
  const isWideLayout = useWideDataLayout();
  const { ref: filterBarMeasureRef } = useElementWidth();
  const [searchInput, setSearchInput] = useState(filters.q || "");
  const [statusOpen, setStatusOpen] = useState(false);
  const [dateOpen, setDateOpen] = useState(false);
  const [categoryOpen, setCategoryOpen] = useState(false);
  const [sortOpen, setSortOpen] = useState(false);
  const [compactFilterOpenInternal, setCompactFilterOpenInternal] =
    useState(false);
  const compactFilterOpen = filterMenuOpen ?? compactFilterOpenInternal;
  const setCompactFilterOpen = (open: boolean) => {
    onFilterMenuOpenChange?.(open);
    if (filterMenuOpen === undefined) {
      setCompactFilterOpenInternal(open);
    }
  };
  const [customDateOpen, setCustomDateOpen] = useState(false);
  const [customFrom, setCustomFrom] = useState(filters.from || "");
  const [customTo, setCustomTo] = useState(filters.to || "");

  const statusRef = useRef<HTMLDivElement>(null);
  const dateRef = useRef<HTMLDivElement>(null);
  const categoryRef = useRef<HTMLDivElement>(null);
  const sortRef = useRef<HTMLDivElement>(null);
  const statusMenuRef = useRef<HTMLDivElement>(null);
  const dateMenuRef = useRef<HTMLDivElement>(null);
  const categoryMenuRef = useRef<HTMLDivElement>(null);
  const sortMenuRef = useRef<HTMLDivElement>(null);
  const compactFilterRef = useRef<HTMLDivElement>(null);
  const compactFilterMenuRef = useRef<HTMLDivElement>(null);
  const filterRailOuterRef = useRef<HTMLDivElement>(null);

  // Debounce search input
  useEffect(() => {
    const t = setTimeout(() => {
      if (searchInput !== (filters.q || "")) {
        setFilter("q", searchInput || null);
      }
    }, 300);
    return () => clearTimeout(t);
  }, [searchInput, filters.q, setFilter]);

  // Click outside to close dropdowns (include menu nodes rendered outside scroll rail)
  useEffect(() => {
    const isInside = (
      anchor: RefObject<HTMLElement | null>,
      menu: RefObject<HTMLElement | null>,
      target: Node,
    ) =>
      Boolean(
        anchor.current?.contains(target) || menu.current?.contains(target),
      );

    const handleClickOutside = (event: MouseEvent) => {
      if (!isWideLayout) return;

      const target = event.target as Node;
      if (!isInside(statusRef, statusMenuRef, target)) setStatusOpen(false);
      if (!isInside(dateRef, dateMenuRef, target)) setDateOpen(false);
      if (!isInside(categoryRef, categoryMenuRef, target))
        setCategoryOpen(false);
      if (!isInside(sortRef, sortMenuRef, target)) setSortOpen(false);
    };
    document.addEventListener("click", handleClickOutside);
    return () => document.removeEventListener("click", handleClickOutside);
  }, [isWideLayout]);

  useEffect(() => {
    setSearchInput(filters.q || "");
  }, [filters.q]);

  useEffect(() => {
    setCustomFrom(filters.from || "");
    setCustomTo(filters.to || "");
  }, [filters.from, filters.to]);

  const sortableCols = schema.columns.filter((c) => c.sortable);
  const hasCategoryFilter = (schema.filterOptions.categories?.length ?? 0) > 0;
  const categorySectionLabel =
    schema.filterOptions.categoryLabel?.trim() || "หมวดหมู่";
  const activeDateLabel = getActiveDateLabel(
    filters,
    schema.filterOptions.datePresets || [],
  );
  const hasActiveFilters = Boolean(
    filters.status ||
    filters.from ||
    filters.to ||
    filters.q ||
    filters.category ||
    filters.sort !== schema.defaultSort.col ||
    filters.dir !== schema.defaultSort.dir,
  );
  const searchLabels = schema.searchFields
    .map((fieldId) => schema.columns.find((col) => col.id === fieldId)?.header)
    .filter(Boolean)
    .slice(0, 3)
    .join(" / ");
  const workerLabel = stripWorkerLabelEmoji(schema.workerLabel);
  const compactSearchPlaceholder = `ค้นหาใน${workerLabel}`;
  const sortColHeader =
    schema.columns.find((c) => c.id === filters.sort)?.header || "วันที่";
  const compactSortLabel = `${sortColHeader} • ${
    filters.dir === "asc" ? "เก่าไปใหม่" : "ใหม่ไปเก่า"
  }`;
  const compactFilterActive = Boolean(
    filters.status ||
      filters.from ||
      filters.to ||
      filters.category,
  );
  const compactFilterCount = [
    filters.status,
    filters.from || filters.to,
    filters.category,
  ].filter(Boolean).length;
  const compactFilterLabel =
    compactFilterCount > 0 ? `ตัวกรอง (${compactFilterCount})` : "ตัวกรอง";
  const datePresets = schema.filterOptions.datePresets || [];
  const selectedDatePreset = matchingDatePreset(
    filters.from,
    filters.to,
    datePresets,
  );
  const showCustomDate =
    customDateOpen || selectedDatePreset === "กำหนดเอง";
  const clearCompactFilters = () => {
    setFilter("status", null);
    setFilter("from", null);
    setFilter("to", null);
    setFilter("category", null);
    setCustomFrom("");
    setCustomTo("");
    setCustomDateOpen(false);
  };

  const searchField = selectionState ? (
            <div className="flex flex-wrap items-center gap-1.5">
              <div className="inline-flex items-center rounded-full bg-aera-warmcream px-3 py-1.5 text-sm font-medium tracking-tight text-aera-amberdark">
                {selectionState.count} รายการที่เลือก
              </div>

              {selectionState.onBulkCancel && (
                <AeraButton
                  variant="danger"
                  size="xs"
                  fontWeight="medium"
                  icon={<CircleX className="h-3.5 w-3.5 shrink-0" />}
                  onClick={selectionState.onBulkCancel}
                  className="gap-1!  border-0!"
                >
                  ยกเลิกรายการที่เลือก
                </AeraButton>
              )}

              <AeraButton
                variant="transparent"
                size="xs"
                fontWeight="medium"
                onClick={selectionState.onClear}
              >
                ล้างการเลือก
              </AeraButton>
            </div>
          ) : (
            <DataTableSearchField
              value={searchInput}
              onChange={setSearchInput}
              placeholder={
                isWideLayout
                  ? `ค้นหา${searchLabels ? ` ${searchLabels}` : ""}...`
                  : compactSearchPlaceholder
              }
              inputRef={searchInputRef}
              variant={isWideLayout ? "wide" : "compact"}
            />
          );

  const filterButton = (
    <FilterButton
      icon={ListFilter}
      label={compactFilterLabel}
      active={compactFilterActive}
      isOpen={compactFilterOpen}
      variant={filterButtonVariant}
      onClick={() => {
        setCompactFilterOpen(!compactFilterOpen);
        setSortOpen(false);
      }}
    />
  );

  return (
    <div
      ref={filterBarMeasureRef}
      data-testid={dataTestId}
      className={cn(
        "flex min-w-0 flex-col border-b border-aera-lightgray bg-aera-white px-3 py-3 min-[900px]:px-4",
        hasActiveFilters ? "gap-3" : "gap-2",
      )}
    >
      <div
        className={cn(
          "flex min-w-0 gap-2",
          isWideLayout ? "flex-row items-center" : "flex-col",
        )}
      >
        {!hideSearch && !isWideLayout ? (
          <div
            className={cn(
              "w-full min-w-0",
              isWideLayout && "min-w-0 flex-1 basis-0",
            )}
          >
            {searchField}
          </div>
        ) : null}

        {!isWideLayout ? (
          <div
            ref={filterRailOuterRef}
            className="flex w-full min-w-0 items-center justify-between gap-2"
          >
            <div className="shrink-0" ref={compactFilterRef}>
              {filterButton}
            </div>
            <div className="flex min-w-0 items-center gap-1.5">
              <span className="shrink-0 text-xs font-medium text-aera-gray">
                เรียงตาม
              </span>
              <div className="min-w-0 shrink" ref={sortRef}>
                <FilterButton
                  icon={ArrowUpDown}
                  label={compactSortLabel}
                  isOpen={sortOpen}
                  active={
                    filters.sort !== schema.defaultSort.col ||
                    filters.dir !== schema.defaultSort.dir
                  }
                  onClick={() => {
                    setSortOpen(!sortOpen);
                    setCompactFilterOpen(false);
                  }}
                />
              </div>
            </div>
          </div>
        ) : (
          <div
            ref={filterRailOuterRef}
            className="flex w-full min-w-0 items-center justify-between gap-3"
          >
            <div className="shrink-0" ref={compactFilterRef}>
              {filterButton}
            </div>
            {rangeLabel ? (
              <span className="shrink-0 text-xs font-medium text-aera-gray">
                {rangeLabel}
              </span>
            ) : null}
          </div>
        )}

        {false ? (
        <div
          ref={filterRailOuterRef}
          className="relative ml-auto min-w-0 w-auto shrink-0"
        >
          <CardScrollRail
            outerRef={filterRailOuterRef}
            syncKey={isWideLayout}
            scrollStep={180}
            pinRailWidthToOuter
            railClassName="aera-dt-filter-rail min-w-0 overflow-x-auto overscroll-x-contain"
            iconClassName="h-4 w-4"
            leftAriaLabel="เลื่อนฟิลเตอร์ซ้าย"
            rightAriaLabel="เลื่อนฟิลเตอร์ขวา"
          >
            <div
              className={cn(
                "inline-flex w-max max-w-none flex-nowrap items-center gap-1.5",
                isWideLayout && "ml-auto",
              )}
            >
              <div className="shrink-0" ref={statusRef}>
                <FilterButton
                  icon={ListFilter}
                  label="สถานะ"
                  active={!!filters.status}
                  isOpen={statusOpen}
                  onClick={() => {
                    setStatusOpen(!statusOpen);
                    setDateOpen(false);
                    setCategoryOpen(false);
                    setSortOpen(false);
                  }}
                />
              </div>

              <div className="shrink-0" ref={dateRef}>
                <FilterButton
                  icon={Calendar}
                  label="วันที่"
                  active={!!filters.from || !!filters.to}
                  isOpen={dateOpen}
                  onClick={() => {
                    setDateOpen(!dateOpen);
                    setStatusOpen(false);
                    setCategoryOpen(false);
                    setSortOpen(false);
                  }}
                />
              </div>

              {hasCategoryFilter && (
                <div className="shrink-0" ref={categoryRef}>
                  <FilterButton
                    icon={ListFilter}
                    label={categorySectionLabel}
                    active={!!filters.category}
                    isOpen={categoryOpen}
                    onClick={() => {
                      setCategoryOpen(!categoryOpen);
                      setStatusOpen(false);
                      setDateOpen(false);
                      setSortOpen(false);
                    }}
                  />
                </div>
              )}

              <div className="shrink-0" ref={sortRef}>
                <FilterButton
                  icon={ArrowUpDown}
                  label="จัดเรียง"
                  isOpen={sortOpen}
                  active={
                    filters.sort !== schema.defaultSort.col ||
                    filters.dir !== schema.defaultSort.dir
                  }
                  onClick={() => {
                    setSortOpen(!sortOpen);
                    setStatusOpen(false);
                    setDateOpen(false);
                    setCategoryOpen(false);
                  }}
                />
              </div>
            </div>
          </CardScrollRail>

          <DropdownMenu
            isOpen={statusOpen}
            menuRef={statusMenuRef}
            anchorRef={statusRef}
            outerRef={filterRailOuterRef}
            isWideLayout={isWideLayout}
            title="เลือกสถานะ"
            options={schema.filterOptions.status || []}
            currentValue={filters.status}
            visualStyle="status"
            iconOverrides={iconOverrides}
            onClose={() => setStatusOpen(false)}
            onSelect={(val) => {
              setFilter("status", val);
              setStatusOpen(false);
            }}
          />
          <DateDropdownMenu
            isOpen={dateOpen}
            menuRef={dateMenuRef}
            anchorRef={dateRef}
            outerRef={filterRailOuterRef}
            isWideLayout={isWideLayout}
            options={schema.filterOptions.datePresets || []}
            currentFrom={filters.from}
            currentTo={filters.to}
            customFrom={customFrom}
            customTo={customTo}
            onCustomFromChange={setCustomFrom}
            onCustomToChange={setCustomTo}
            onPresetSelect={(preset) => {
              const range = getDatePresetRange(preset);
              setFilter("from", range.from);
              setFilter("to", range.to);
              // Keep schema default sort (e.g. updated_at) — date range is a
              // filter only, not a sort change.
              setDateOpen(false);
            }}
            onCustomApply={() => {
              setFilter("from", customFrom || null);
              setFilter("to", customTo || null);
              setDateOpen(false);
            }}
            onClear={() => {
              setFilter("from", null);
              setFilter("to", null);
              setCustomFrom("");
              setCustomTo("");
              setDateOpen(false);
            }}
            onClose={() => setDateOpen(false)}
          />
          {hasCategoryFilter ? (
            <DropdownMenu
              isOpen={categoryOpen}
              menuRef={categoryMenuRef}
              anchorRef={categoryRef}
              outerRef={filterRailOuterRef}
              isWideLayout={isWideLayout}
              title={`เลือก${categorySectionLabel}`}
              options={schema.filterOptions.categories || []}
              currentValue={filters.category ?? null}
              onClose={() => setCategoryOpen(false)}
              onSelect={(val) => {
                setFilter("category", val);
                setCategoryOpen(false);
              }}
            />
          ) : null}
          <FilterMenuShell
            title="เรียงตาม"
            isOpen={sortOpen}
            isWideLayout={isWideLayout}
            onClose={() => setSortOpen(false)}
            menuRef={sortMenuRef}
            anchorRef={sortRef}
            outerRef={filterRailOuterRef}
            width="14rem"
            widthClass="w-56"
          >
            {sortableCols.map((col) => (
              <div key={col.id}>
                <AeraButton
                  variant="transparent"
                  size="sm"
                  textColor="gray"
                  fullWidth
                  className="justify-between!"
                  fontWeight="medium"
                  onClick={() => {
                    setFilter("sort", col.id);
                    setFilter("dir", "asc");
                    setSortOpen(false);
                  }}
                  iconRight={
                    filters.sort === col.id &&
                    filters.dir === "asc" && (
                      <Check
                        className="w-4 h-4 text-aera-amber"
                        strokeWidth={3}
                      />
                    )
                  }
                >
                  <span>{col.header} (น้อยไปมาก)</span>
                </AeraButton>
                <AeraButton
                  variant="transparent"
                  size="sm"
                  textColor="gray"
                  fullWidth
                  className="justify-between!"
                  fontWeight="medium"
                  onClick={() => {
                    setFilter("sort", col.id);
                    setFilter("dir", "desc");
                    setSortOpen(false);
                  }}
                  iconRight={
                    filters.sort === col.id &&
                    filters.dir === "desc" && (
                      <Check
                        className="w-4 h-4 text-aera-amber"
                        strokeWidth={3}
                      />
                    )
                  }
                >
                  <span>{col.header} (มากไปน้อย)</span>
                </AeraButton>
              </div>
            ))}
          </FilterMenuShell>
        </div>
        ) : (
          <>
            <FilterMenuShell
              title="ตัวกรอง"
              isOpen={compactFilterOpen}
              isWideLayout={false}
              onClose={() => setCompactFilterOpen(false)}
              menuRef={compactFilterMenuRef}
              anchorRef={compactFilterRef}
              outerRef={filterRailOuterRef}
              width="22rem"
              widthClass="w-88"
              footer={
                <div className="flex items-center gap-2">
                  <AeraButton
                    type="button"
                    variant="outline"
                    size="sm"
                    fontWeight="medium"
                    className="flex-1"
                    onClick={clearCompactFilters}
                  >
                    ล้างตัวกรอง
                  </AeraButton>
                  <AeraButton
                    type="button"
                    variant="primary"
                    size="sm"
                    fontWeight="medium"
                    className="flex-1"
                    onClick={() => {
                      if (showCustomDate) {
                        setFilter("from", customFrom || null);
                        setFilter("to", customTo || null);
                      }
                      setCompactFilterOpen(false);
                    }}
                  >
                    เสร็จสิ้น
                  </AeraButton>
                </div>
              }
            >
              <div className="flex flex-col gap-5">
                <section>
                  <FilterSectionLabel>วันที่</FilterSectionLabel>
                  <div className="flex flex-wrap gap-1.5">
                    <FilterChoiceChip
                      selected={selectedDatePreset === "ทั้งหมด" && !customDateOpen}
                      onClick={() => {
                        setCustomDateOpen(false);
                        setFilter("from", null);
                        setFilter("to", null);
                        setCustomFrom("");
                        setCustomTo("");
                      }}
                    >
                      ทั้งหมด
                    </FilterChoiceChip>
                    {datePresets.map((preset) => {
                      const selected =
                        preset === "กำหนดเอง"
                          ? showCustomDate
                          : selectedDatePreset === preset && !customDateOpen;
                      return (
                        <FilterChoiceChip
                          key={preset}
                          selected={selected}
                          onClick={() => {
                            if (preset === "กำหนดเอง") {
                              setCustomDateOpen(true);
                              return;
                            }
                            setCustomDateOpen(false);
                            const range = getDatePresetRange(preset);
                            setFilter("from", range.from);
                            setFilter("to", range.to);
                          }}
                        >
                          {preset}
                        </FilterChoiceChip>
                      );
                    })}
                  </div>
                  {showCustomDate ? (
                    <div className="mt-3 grid grid-cols-1 gap-2 sm:grid-cols-2">
                      <FilterDateInput
                        value={customFrom}
                        onChange={setCustomFrom}
                      />
                      <FilterDateInput
                        value={customTo}
                        onChange={setCustomTo}
                      />
                    </div>
                  ) : null}
                </section>

                {hasCategoryFilter ? (
                  <section>
                    <FilterSectionLabel>{categorySectionLabel}</FilterSectionLabel>
                    <div className="flex flex-wrap gap-1.5">
                      <FilterChoiceChip
                        selected={!filters.category}
                        onClick={() => setFilter("category", null)}
                      >
                        ทั้งหมด
                      </FilterChoiceChip>
                      {(schema.filterOptions.categories || []).map((opt) => (
                        <FilterChoiceChip
                          key={opt}
                          selected={filters.category === opt}
                          onClick={() =>
                            setFilter(
                              "category",
                              filters.category === opt ? null : opt,
                            )
                          }
                        >
                          {opt}
                        </FilterChoiceChip>
                      ))}
                    </div>
                  </section>
                ) : null}

                {(schema.filterOptions.status || []).length > 0 ? (
                  <section>
                    <FilterSectionLabel>สถานะ</FilterSectionLabel>
                    <div className="flex flex-col gap-0.5">
                      <FilterChoiceRow
                        selected={!filters.status}
                        onClick={() => setFilter("status", null)}
                      >
                        ทั้งหมด
                      </FilterChoiceRow>
                      {(schema.filterOptions.status || []).map((opt) => (
                        <FilterChoiceRow
                          key={opt}
                          selected={filters.status === opt}
                          onClick={() =>
                            setFilter(
                              "status",
                              filters.status === opt ? null : opt,
                            )
                          }
                        >
                          <StatusOptionLabel
                            status={opt}
                            iconOverrides={iconOverrides}
                          />
                        </FilterChoiceRow>
                      ))}
                    </div>
                  </section>
                ) : null}
              </div>
            </FilterMenuShell>
            <FilterMenuShell
              title="เรียงตาม"
              isOpen={sortOpen}
              isWideLayout={false}
              onClose={() => setSortOpen(false)}
              menuRef={sortMenuRef}
              anchorRef={sortRef}
              outerRef={filterRailOuterRef}
              width="14rem"
              widthClass="w-56"
            >
              {sortableCols.map((col) => (
                <div key={col.id}>
                  <AeraButton
                    variant="transparent"
                    size="sm"
                    textColor="gray"
                    fullWidth
                    className="justify-between!"
                    fontWeight="medium"
                    onClick={() => {
                      setFilter("sort", col.id);
                      setFilter("dir", "asc");
                      setSortOpen(false);
                    }}
                    iconRight={
                      filters.sort === col.id &&
                      filters.dir === "asc" && (
                        <Check
                          className="w-4 h-4 text-aera-amber"
                          strokeWidth={3}
                        />
                      )
                    }
                  >
                    <span>{col.header} (น้อยไปมาก)</span>
                  </AeraButton>
                  <AeraButton
                    variant="transparent"
                    size="sm"
                    textColor="gray"
                    fullWidth
                    className="justify-between!"
                    fontWeight="medium"
                    onClick={() => {
                      setFilter("sort", col.id);
                      setFilter("dir", "desc");
                      setSortOpen(false);
                    }}
                    iconRight={
                      filters.sort === col.id &&
                      filters.dir === "desc" && (
                        <Check
                          className="w-4 h-4 text-aera-amber"
                          strokeWidth={3}
                        />
                      )
                    }
                  >
                    <span>{col.header} (มากไปน้อย)</span>
                  </AeraButton>
                </div>
              ))}
            </FilterMenuShell>
          </>
        )}
      </div>

      {/* Active Filters Row */}
      {hasActiveFilters && (
        <div className="flex items-center justify-between">
          <div className="flex flex-wrap gap-2 min-h-[28px]">
            <>
              {filters.status && (
                <ActiveFilter
                  label="สถานะ"
                  value={filters.status}
                  visualStyle="status"
                  iconOverrides={iconOverrides}
                  onClear={() => setFilter("status", null)}
                />
              )}
              {activeDateLabel && (
                <ActiveFilter
                  label="วันที่"
                  value={activeDateLabel}
                  onClear={() => {
                    setFilter("from", null);
                    setFilter("to", null);
                    setCustomFrom("");
                    setCustomTo("");
                  }}
                />
              )}
              {filters.category && (
                <ActiveFilter
                  label={categorySectionLabel}
                  value={filters.category}
                  onClear={() => setFilter("category", null)}
                />
              )}
              {filters.q && (
                <ActiveFilter
                  label="ค้นหา"
                  value={filters.q}
                  onClear={() => {
                    setFilter("q", null);
                    setSearchInput("");
                  }}
                />
              )}
              {(filters.sort !== schema.defaultSort.col ||
                filters.dir !== schema.defaultSort.dir) && (
                <ActiveFilter
                  label="เรียง"
                  value={`${schema.columns.find((c) => c.id === filters.sort)?.header || ""} ${filters.dir === "asc" ? "↑" : "↓"}`}
                  onClear={() => {
                    setFilter("sort", schema.defaultSort.col);
                    setFilter("dir", schema.defaultSort.dir);
                  }}
                />
              )}
              <AeraButton
                variant="transparent"
                size="xs"
                fontWeight="medium"
                onClick={() => {
                  setFilter("status", null);
                  setFilter("from", null);
                  setFilter("to", null);
                  setFilter("q", null);
                  setFilter("category", null);
                  setFilter(SIMILAR_NAME_FILTER_KEY, null);
                  setFilter("sort", schema.defaultSort.col);
                  setFilter("dir", schema.defaultSort.dir);
                  setSearchInput("");
                  setCustomFrom("");
                  setCustomTo("");
                }}
              >
                ล้างทั้งหมด
              </AeraButton>
            </>
          </div>
        </div>
      )}
    </div>
  );
}

function useAnchoredFilterMenuStyle(
  anchorRef: RefObject<HTMLElement | null>,
  outerRef: RefObject<HTMLElement | null>,
  width: string,
  enabled: boolean,
  isWideLayout: boolean,
) {
  const [style, setStyle] = useState<CSSProperties>({});

  useLayoutEffect(() => {
    if (!enabled) return;

    const update = () => {
      if (!isWideLayout) {
        setStyle({});
        return;
      }

      const anchor = anchorRef.current;
      const outer = outerRef.current;
      if (!anchor || !outer) return;

      const anchorRect = anchor.getBoundingClientRect();
      const outerRect = outer.getBoundingClientRect();
      setStyle({
        position: "absolute",
        top: anchorRect.bottom - outerRect.top + 8,
        right: outerRect.right - anchorRect.right,
        width,
        zIndex: 9997,
      });
    };

    update();
    const observer = new ResizeObserver(update);
    if (anchorRef.current) observer.observe(anchorRef.current);
    if (outerRef.current) observer.observe(outerRef.current);
    window.addEventListener("resize", update);
    window.addEventListener("scroll", update, true);

    return () => {
      observer.disconnect();
      window.removeEventListener("resize", update);
      window.removeEventListener("scroll", update, true);
    };
  }, [anchorRef, outerRef, enabled, isWideLayout, width]);

  return style;
}

function DropdownMenu({
  isOpen,
  menuRef,
  anchorRef,
  outerRef,
  title,
  options,
  currentValue,
  onSelect,
  visualStyle = "default",
  iconOverrides,
  onClose,
  isWideLayout,
}: {
  isOpen: boolean;
  menuRef: RefObject<HTMLDivElement | null>;
  anchorRef: RefObject<HTMLElement | null>;
  outerRef: RefObject<HTMLElement | null>;
  title: string;
  options: string[];
  currentValue: string | null;
  onSelect: (val: string | null) => void;
  visualStyle?: "default" | "status";
  iconOverrides?: IconOverrideMap;
  onClose: () => void;
  isWideLayout: boolean;
}) {
  return (
    <FilterMenuShell
      title={title}
      isOpen={isOpen}
      isWideLayout={isWideLayout}
      onClose={onClose}
      menuRef={menuRef}
      anchorRef={anchorRef}
      outerRef={outerRef}
      width="14rem"
      widthClass="w-56"
    >
      {options.map((opt) => (
        <AeraButton
          variant="transparent"
          size="sm"
          textColor="gray"
          fullWidth
          className="justify-between!"
          fontWeight="medium"
          key={opt}
          onClick={() => onSelect(currentValue === opt ? null : opt)}
          iconRight={
            currentValue === opt && (
              <Check className="w-4 h-4 text-aera-amber" strokeWidth={3} />
            )
          }
        >
          {visualStyle === "status" ? (
            <StatusOptionLabel status={opt} iconOverrides={iconOverrides} />
          ) : (
            <span>{opt}</span>
          )}
        </AeraButton>
      ))}
    </FilterMenuShell>
  );
}

function StatusOptionLabel({
  status,
  iconOverrides,
}: {
  status: string;
  iconOverrides?: IconOverrideMap;
}) {
  const { iconKey, label, tone } = resolveStatusVisual(status);
  const toneClass = {
    success: "text-aera-green",
    warning: "text-aera-amber",
    danger: "text-aera-red",
    inverse: "text-aera-red",
    neutral: "text-aera-charcoal",
  }[tone];

  return (
    <span
      className={cn("inline-flex items-center gap-2 font-medium", toneClass)}
    >
      <ResolvedIcon
        iconKey={iconKey}
        overrides={iconOverrides}
        className="h-4 w-4 shrink-0"
      />
      <span>{label}</span>
    </span>
  );
}

function openDatePicker(input: HTMLInputElement) {
  try {
    if (typeof input.showPicker === "function") {
      input.showPicker();
      return;
    }
  } catch {
    // showPicker can throw if not triggered by a user gesture in some browsers
  }
  input.focus();
}

function FilterDateInput({
  value,
  onChange,
}: {
  value: string;
  onChange: (value: string) => void;
}) {
  const inputRef = useRef<HTMLInputElement>(null);

  return (
    <div
      className="relative w-full cursor-pointer"
      onClick={() => {
        const input = inputRef.current;
        if (input) openDatePicker(input);
      }}
    >
      <input
        ref={inputRef}
        type="date"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="aera-dt-date-input w-full rounded-lg bg-aera-lightgray/60 px-3 py-2 pr-10 text-sm text-aera-charcoal outline-none"
      />
      <Calendar
        className="pointer-events-none absolute top-1/2 right-3 h-4 w-4 -translate-y-1/2 text-aera-charcoal"
        aria-hidden
      />
    </div>
  );
}

function DateDropdownMenu({
  isOpen,
  menuRef,
  anchorRef,
  outerRef,
  options,
  currentFrom,
  currentTo,
  customFrom,
  customTo,
  onCustomFromChange,
  onCustomToChange,
  onPresetSelect,
  onCustomApply,
  onClear,
  onClose,
  isWideLayout,
}: {
  isOpen: boolean;
  menuRef: RefObject<HTMLDivElement | null>;
  anchorRef: RefObject<HTMLElement | null>;
  outerRef: RefObject<HTMLElement | null>;
  options: string[];
  currentFrom: string | null;
  currentTo: string | null;
  customFrom: string;
  customTo: string;
  onCustomFromChange: (value: string) => void;
  onCustomToChange: (value: string) => void;
  onPresetSelect: (preset: string) => void;
  onCustomApply: () => void;
  onClear: () => void;
  onClose: () => void;
  isWideLayout: boolean;
}) {
  return (
    <FilterMenuShell
      title="ช่วงเวลา"
      isOpen={isOpen}
      isWideLayout={isWideLayout}
      onClose={onClose}
      menuRef={menuRef}
      anchorRef={anchorRef}
      outerRef={outerRef}
      width="18rem"
      widthClass="w-72"
    >
      {options
        .filter((opt) => opt !== "กำหนดเอง")
        .map((opt) => {
          const range = getDatePresetRange(opt);
          const selected = range.from === currentFrom && range.to === currentTo;
          return (
            <AeraButton
              variant="transparent"
              size="sm"
              textColor="gray"
              fullWidth
              className="justify-between!"
              fontWeight="medium"
              key={opt}
              onClick={() => onPresetSelect(opt)}
              iconRight={
                selected && (
                  <Check className="w-4 h-4 text-aera-amber" strokeWidth={3} />
                )
              }
            >
              <span>{opt}</span>
            </AeraButton>
          );
        })}
      {options.includes("กำหนดเอง") && (
        <div className="mt-3 px-3">
          <p className="text-xs font-bold text-aera-charcoal uppercase tracking-widest mb-3">
            กำหนดเอง
          </p>
          <div
            className={cn(
              "aera-dt-date-custom-grid grid grid-cols-1 gap-2",
              isWideLayout && "sm:grid-cols-2",
            )}
          >
            <FilterDateInput value={customFrom} onChange={onCustomFromChange} />
            <FilterDateInput value={customTo} onChange={onCustomToChange} />
          </div>
          <div className="mt-2 flex flex-col gap-2 sm:flex-row">
            <AeraButton
              variant="primary"
              size="sm"
              fontWeight="medium"
              onClick={onCustomApply}
              className="flex-1"
            >
              ใช้ช่วงนี้
            </AeraButton>
            <AeraButton
              variant="outline"
              size="sm"
              fontWeight="medium"
              className="flex-1"
              onClick={onClear}
            >
              ล้าง
            </AeraButton>
          </div>
        </div>
      )}
    </FilterMenuShell>
  );
}

function FilterButton({
  icon: Icon,
  label,
  active = false,
  isOpen = false,
  variant = "transparent",
  onClick,
}: {
  icon: LucideIcon;
  label: string;
  active?: boolean;
  isOpen?: boolean;
  variant?: "transparent" | "outline";
  onClick?: () => void;
}) {
  return (
    <AeraButton
      variant={variant === "outline" ? "outline" : "transparent"}
      size="sm"
      fontWeight="medium"
      onClick={onClick}
      className={cn(
        "shrink-0 gap-1! whitespace-nowrap active:scale-95",
        variant === "transparent" &&
          (isOpen || active ? "bg-aera-amberlight/50" : "bg-transparent"),
        variant === "transparent" && active && "text-aera-amberdark",
        variant === "outline" &&
          (isOpen || active) &&
          "border-aera-amber bg-aera-warmcream text-aera-amberdark",
      )}
      icon={
        <Icon
          className={cn(
            "h-4 w-4",
            active ? "text-aera-amberdark" : "text-aera-gray",
          )}
        />
      }
      iconRight={
        <span className="inline-flex items-center gap-1">
          {active ? (
            <span
              className="h-1.5 w-1.5 rounded-full bg-aera-amber"
              aria-hidden
            />
          ) : null}
          <ChevronDown className="h-3.5 w-3.5 text-aera-gray" />
        </span>
      }
    >
      {label}
    </AeraButton>
  );
}

function ActiveFilter({
  label,
  value,
  onClear,
  visualStyle = "default",
  iconOverrides,
}: {
  label: string;
  value: string;
  onClear: () => void;
  visualStyle?: "default" | "status";
  iconOverrides?: IconOverrideMap;
}) {
  const visual = visualStyle === "status" ? resolveStatusVisual(value) : null;
  const toneClass = visual
    ? {
        success: "text-aera-green",
        warning: "text-aera-amber",
        danger: "text-aera-red",
        inverse: "text-aera-charcoal",
        neutral: "text-aera-gray",
      }[visual.tone]
    : null;

  return (
    <div className="flex items-center gap-1.5 text-xs bg-aera-lightgray/50 text-aera-charcoal px-2 py-1 rounded-md animate-in fade-in zoom-in-95 duration-200">
      <span className="font-semibold text-aera-charcoal">{label}:</span>
      {visual ? (
        <span
          className={cn(
            "inline-flex items-center gap-1.5 font-bold",
            toneClass,
          )}
        >
          <ResolvedIcon
            iconKey={visual.iconKey}
            overrides={iconOverrides}
            className="h-3.5 w-3.5 shrink-0"
          />
          <span>{visual.label}</span>
        </span>
      ) : (
        <span className="font-bold">{value}</span>
      )}
      <AeraButton
        variant="transparent"
        size="xxs"
        onClick={onClear}
        icon={<X className="w-3 h-3" />}
      ></AeraButton>
    </div>
  );
}
